import { parseZonedDateTime } from '@internationalized/date';
import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import { LoadStatus, Weight } from '@rematter/frontend-lib-api-client';

import {
  BrokeredLoadLineItemRecord,
  BrokeredLoadRecord,
} from '../types/brokeredLoad';

import { toMoney } from '~/modules/trade/utils/money';
import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const brokeredLoadDetailQueryAtom = createQueryAtomFactory(
  'brokerage.brokeredLoadDetail',
  (params: { loadId: string }) => ({
    load: {
      __args: {
        id: params.loadId,
      },
      id: true,
      status: true,
      reviewed: true,
      expectedShipDate: true,
      shippedDate: true,
      loadReceivedDate: true,
      documentNumber: true,
      supplierId: true,
      supplier: {
        id: true,
        name: true,
      },
      brokeredOrderId: true,
      brokeredOrder: {
        id: true,
        orderNumber: true,
      },
      salesOrderId: true,
      salesOrder: {
        id: true,
        documentNumber: true,
        account: {
          id: true,
          name: true,
        },
      },
      bookingId: true,
      booking: {
        id: true,
        documentNumber: true,
      },
      facilityId: true,
      deliveryNumber: true,
      releaseNumber: true,
      truckNumber: true,
      trailerNumber: true,
      containerNumber: true,
      sealNumber: true,
      heavyWeight: {
        value: true,
        unit: true,
      },
      lightWeight: {
        value: true,
        unit: true,
      },
      recyclerInternalNotes: true,
      recyclerExternalNotes: true,
      customerInternalNotes: true,
      customerExternalNotes: true,
      weightDisplayUnit: true,
      recyclerFiles: {
        id: true,
        fileName: true,
        fileSize: true,
        mimeType: true,
        status: true,
        entityType: true,
        entityId: true,
        metadata: true,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      lineItems: {
        id: true,
        itemId: true,
        type: true,
        item: {
          id: true,
          name: true,
          skuNumber: true,
        },
        purchaseOrderId: true,
        purchaseOrder: {
          id: true,
          documentNumber: true,
        },
        phase: true,
        unitPrice: {
          amount: true,
          currencyCode: true,
        },
        estimatedPrice: {
          amount: true,
          currencyCode: true,
        },
        pricingUnit: true,
        weights: true,
      },
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
  }),
  {
    select: (data): BrokeredLoadRecord | null => {
      const load = data?.load;
      if (!load || !load.supplierId) return null;

      return {
        id: load.id,
        loadNumber: load.documentNumber.toString(),
        status: load.status as LoadStatus,
        reviewed: load.reviewed ?? false,
        expectedShipDate: maybeParseCalendarDate(load.expectedShipDate),
        shippedDate: maybeParseCalendarDate(load.shippedDate),
        loadReceivedDate: maybeParseCalendarDate(load.loadReceivedDate),
        salesOrderId: load.salesOrderId ?? undefined,
        salesOrderNumber: load.salesOrder?.documentNumber ?? undefined,
        bookingId: load.bookingId ?? undefined,
        bookingNumber: load.booking?.documentNumber ?? undefined,
        facilityId: load.facilityId ?? undefined,
        supplierId: load.supplierId as string,
        supplier: load.supplier as { id: string; name: string },
        brokeredOrderId: load.brokeredOrderId ?? undefined,
        brokeredOrder: load.brokeredOrder ?? undefined,
        customerName: load.salesOrder?.account?.name ?? undefined,
        deliveryNumber: load.deliveryNumber ?? undefined,
        releaseNumber: load.releaseNumber ?? undefined,
        truckNumber: load.truckNumber ?? undefined,
        trailerNumber: load.trailerNumber ?? undefined,
        containerNumber: load.containerNumber ?? undefined,
        sealNumber: load.sealNumber ?? undefined,
        heavyWeight: load.heavyWeight
          ? { ...load.heavyWeight, __typename: 'Weight' as const }
          : undefined,
        lightWeight: load.lightWeight
          ? { ...load.lightWeight, __typename: 'Weight' as const }
          : undefined,
        recyclerInternalNotes: load.recyclerInternalNotes ?? undefined,
        recyclerExternalNotes: load.recyclerExternalNotes ?? undefined,
        customerInternalNotes: load.customerInternalNotes ?? undefined,
        customerExternalNotes: load.customerExternalNotes ?? undefined,
        weightDisplayUnit: load.weightDisplayUnit ?? undefined,
        files: load.recyclerFiles?.map((file) => ({
          id: file.id,
          fileName: file.fileName,
          fileSize: file.fileSize ?? 0,
          mimeType: file.mimeType,
          status: file.status,
          entityType: file.entityType ?? undefined,
          entityId: file.entityId ?? undefined,
          metadata: file.metadata as Record<string, unknown>,
          createdAt: parseZonedDateTime(file.timestamps.createdAt),
          updatedAt: parseZonedDateTime(file.timestamps.updatedAt),
        })),
        lineItems: load.lineItems.map((lineItem) => ({
          id: lineItem.id,
          itemId: lineItem.itemId ?? null,
          type: lineItem.type,
          loadId: load.id,
          item: lineItem.item ?? null,
          purchaseOrderId: lineItem.purchaseOrderId ?? null,
          purchaseOrder: lineItem.purchaseOrder ?? null,
          phase: lineItem.phase,
          unitPrice: lineItem.unitPrice ? toMoney(lineItem.unitPrice) : null,
          estimatedPrice: lineItem.estimatedPrice
            ? toMoney(lineItem.estimatedPrice)
            : null,
          pricingUnit:
            lineItem.pricingUnit as BrokeredLoadLineItemRecord['pricingUnit'],
          weights: lineItem.weights as Record<string, Weight>,
        })),
        timestamps: {
          createdAt: parseZonedDateTime(load.timestamps.createdAt),
          updatedAt: load.timestamps.updatedAt
            ? parseZonedDateTime(load.timestamps.updatedAt)
            : undefined,
        },
      };
    },
  },
);
